import {
    FetchState,
    IdentityChallengeState,
    KbaQuizQuestion,
    KbaQuizAnswer,
    VerifyIdentityResponse,
    VerifyIdentityStatus,
    UserOnboardingSteps
} from "@/shared/types";
import {
    postValidateKbaQuiz,
    postVerifyIdentityRequest,
    postValidateKbaChallengeQuiz
} from "@/shared/api/fraudDetectionService";
import * as storeHelper from "@/shared/storeHelper";
import { log } from "@/shared/datadog";
import { BankCustodian } from "@/admin/types";
import { defineStore } from "pinia";
import _ from "lodash";
import { useWizardStore } from "@/employee/store/wizard";

const initialState: IdentityChallengeState = {
    fetchState: FetchState.Uninitialized,
    questions: [],
    answers: [],
    verifyIdentityStatus: null,
    verifyIdentityIdNumber: null,
    hasAnsweredWave1: false,
    documentsFormUrl: null
};

const toValidStatusOrError = (s: string): VerifyIdentityStatus => {
    const statusIs = (status: VerifyIdentityStatus) => status.toUpperCase() === s.toUpperCase() ? status : null;

    const validStatus =
        statusIs("Pass") ||
        statusIs("Fail") ||
        statusIs("NeedInfo") ||
        statusIs("TimedOut") ||
        statusIs("RetryLimitReached") ||
        statusIs("PromptSsn") ||
        statusIs("PromptDob") ||
        statusIs("PromptDobAndSsn") ||
        statusIs("DocumentsRequested") ||
        statusIs("Error");

    if (validStatus === null) {
        log(`could not parse FraudDetection VerifyIdentityStatus: ${s}`, { status: s }, "warning");
        return "Error";
    }

    return validStatus;
};

export const useIdentityChallengeStore = defineStore("identityChallenge", {
    state: (): IdentityChallengeState => (_.cloneDeep(initialState)),
    actions: {
        setFetchState (newFetchState: FetchState) {
            return this.fetchState = newFetchState;
        },
        setQuestions (newQuestions: KbaQuizQuestion[]) {
            this.questions = (newQuestions || []);
        },
        initializeAnswers (questions: KbaQuizQuestion[]) {
            this.answers = [];
            if (questions && questions.length > 0) {
                questions.forEach(_ => {
                    this.answers.push(null);
                });
            }
        },
        setIdNumber (idNumber: string) {
            return this.verifyIdentityIdNumber = idNumber;
        },
        setAnswerForQuestion ({ index, answer }: { index: number, answer: string }) {
            this.answers[index] = answer;
            this.answers = [ ...this.answers ]; // force computed properties to update
        },

        setVerifyIdentityStatus (newStatus: VerifyIdentityStatus) {
            return this.verifyIdentityStatus = toValidStatusOrError(newStatus);
        },

        setDidAnswerWave1Questions (didAnswer: boolean) {
            return this.hasAnsweredWave1 = didAnswer;
        },

        setDocumentsFormUrl (url: string) {
            return this.documentsFormUrl = url;
        },
        getChallengeQuestions (
            payload: { userCustodian: BankCustodian, isDirectSignup: boolean }) {
            if (this.fetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            const onSuccess = (data: VerifyIdentityResponse) => {
                this.setQuestions(data.kbaQuizQuestions);
                this.initializeAnswers(data.kbaQuizQuestions);
                this.setIdNumber(data.idNumber);
                this.setFetchState(FetchState.Loaded);
                this.setVerifyIdentityStatus(data.status);
                this.setDocumentsFormUrl(data.documentsFormUrl);
                if (data.status === "DocumentsRequested" && !data.documentsFormUrl) {
                    this.setVerifyIdentityStatus("Error");
                }

                if (data.status === "Pass") {
                    const wizardStore = useWizardStore();
                    wizardStore.setStepComplete(UserOnboardingSteps.VerifyIdentity);
                }

                return null;
            };

            const tryGetChallengeQuestions = new Promise((resolve, reject) => {
                let attemptsLeft = 5;
                const action = (): void => {
                    postVerifyIdentityRequest(payload.userCustodian, payload.isDirectSignup)
                        .then(response => {
                            if (response.data) {
                                onSuccess(response.data);
                            }

                            return null;
                        })
                        .catch(err => {
                            attemptsLeft--;
                            if (attemptsLeft > 0 && err && err.response && err.response.status === 404) {
                                // user is not in FraudDetectionService DB yet, keep trying
                                window.setTimeout(action, 3000);
                                return;
                            }

                            this.setVerifyIdentityStatus("Error");
                            this.setFetchState(FetchState.Uninitialized);
                            reject(err);
                        });
                };

                action();
            });

            return tryGetChallengeQuestions;
        },

        submitWave1Answers () {
            return Promise.resolve()
                .then(() => {
                    if (!!this.verifyIdentityIdNumber && this.answers.every(x => !!x)) {
                        this.setFetchState(FetchState.Loading);
                        const kbaQuizAnswers: KbaQuizAnswer[] = this.questions.map((value, index) => ({
                            questionType: value.questionType,
                            answer: this.answers[index]
                        }));

                        return postValidateKbaQuiz({
                            kbaQuizAnswers,
                            idNumber: this.verifyIdentityIdNumber || ""
                        })
                            .then(response => {
                                this.setQuestions(response.data.kbaQuizQuestions);
                                this.initializeAnswers(response.data.kbaQuizQuestions);
                                this.setIdNumber(response.data.idNumber);
                                this.setVerifyIdentityStatus(response.data.status);
                                this.setFetchState(FetchState.Loaded);
                                this.setDidAnswerWave1Questions(true);
                                return null;
                            });
                    }
                })
                .catch(err => {
                    this.setFetchState(FetchState.Uninitialized);
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        submitWave2Answers () {
            return Promise.resolve()
                .then(() => {
                    if (!!this.verifyIdentityIdNumber && this.answers.every(x => !!x)) {
                        this.setFetchState(FetchState.Loading);
                        const kbaQuizAnswers: KbaQuizAnswer[] = this.questions.map((value, index) => ({
                            questionType: value.questionType,
                            answer: this.answers[index]
                        }));

                        return postValidateKbaChallengeQuiz({
                            kbaQuizAnswers,
                            idNumber: this.verifyIdentityIdNumber || ""
                        })
                            .then(response => {
                                this.setQuestions(response.data.kbaQuizQuestions);
                                this.initializeAnswers(response.data.kbaQuizQuestions);
                                this.setIdNumber(response.data.idNumber);
                                this.setVerifyIdentityStatus(response.data.status);
                                this.setFetchState(FetchState.Loaded);
                                return null;
                            });
                    }
                })
                .catch(err => {
                    this.setFetchState(FetchState.Uninitialized);
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});
